import { unreachable } from '../../../ts-utils';
import { memoize } from 'lodash';
import { IdentityType } from '../../../controller/services/comments-service-types';
const getCommentOwnerById = (state, resourceId) => (commentId) => {
    const resourceState = state.comments[resourceId];
    if ((resourceState === null || resourceState === void 0 ? void 0 : resourceState.type) !== 'READY') {
        return ownerCache.getUnknown();
    }
    const comment = resourceState.commentsById[commentId];
    if (!comment) {
        return ownerCache.getUnknown();
    }
    return getOwner(comment, state.members);
};
export const getCurrentOwner = (state) => state.members.currentOwner;
export const getCurrentMember = (state) => {
    const currentOwner = state.members.currentOwner;
    return currentOwner.type === 'MEMBER' ? currentOwner.member : undefined;
};
export const getMemberCache = (state) => state.members.membersCache;
export const getIsCurrentUserAuthenticated = (state) => { var _a; return ((_a = getCurrentMember(state)) === null || _a === void 0 ? void 0 : _a.profile) !== undefined; };
export const getMemberById = (state, memberId) => memberId ? state.members.membersCache[memberId] : undefined;
export const getIsMemberCheckPending = (state) => state.members.memberCheckPending;
export const getMemberBadge = (state, memberId, index = 0) => {
    var _a, _b;
    const member = getMemberById(state, memberId);
    return (_b = (_a = member === null || member === void 0 ? void 0 : member.memberBadges) === null || _a === void 0 ? void 0 : _a[index]) !== null && _b !== void 0 ? _b : undefined;
};
export const getMemberRole = (state, memberId, index = 0) => {
    var _a, _b;
    const member = getMemberById(state, memberId);
    return (_b = (_a = member === null || member === void 0 ? void 0 : member.memberRoles) === null || _a === void 0 ? void 0 : _a[index]) !== null && _b !== void 0 ? _b : undefined;
};
export const getMemberRoles = (state, memberId) => {
    var _a;
    const member = getMemberById(state, memberId);
    return (_a = member === null || member === void 0 ? void 0 : member.memberRoles) !== null && _a !== void 0 ? _a : [];
};
export const getMembers = (state, memberIds) => {
    return Object.values(state.members.membersCache).filter((member) => memberIds.includes(member.id));
};
// @TODO: have more expicit handling of dummy member
export const getPendingMemberDummy = () => {
    return {
        profile: {
            nickname: '\u00A0',
        },
    };
};
// To have better render optimization we do cache 'owner' objects whenever possible
const ownerCache = (() => {
    const ownerMemberCache = new WeakMap();
    const getAnonymous = memoize((id) => ({ type: 'ANONYMOUS', identityId: id }));
    const UNKNOWN_OWNER = { type: 'UNKNOWN' };
    const PLACEHOLDER_OWNER = { type: 'PLACEHOLDER' };
    return {
        getMember: (member) => {
            if (ownerMemberCache.has(member)) {
                return ownerMemberCache.get(member);
            }
            else {
                const owner = { type: 'MEMBER', member };
                ownerMemberCache.set(member, owner);
                return owner;
            }
        },
        getAnonymous,
        getUnknown: () => UNKNOWN_OWNER,
        getPlaceholder: () => PLACEHOLDER_OWNER,
    };
})();
export const getOwnerForReplyingTo = (state, resourceId, replyState) => {
    var _a;
    const membersState = state.members;
    switch (replyState.type) {
        case 'REPLY_TYPING':
        case 'REPLY_CREATE_PENDING':
        case 'REPLY_CREATE_ERROR':
        case 'REPLY_CREATE_ERROR_EDITING':
        case 'REPLY_DELETED':
        case 'REPLY_LIVE_PENDING':
            const { targetId } = replyState;
            const owner = getCommentOwnerById(state, resourceId)(targetId);
            return owner;
        default:
            const createdBy = (_a = replyState.comment.replyTo) === null || _a === void 0 ? void 0 : _a.createdBy;
            return resolveOwner({ createdBy, membersState, fallback: ownerCache.getUnknown() });
    }
};
export const getOwner = (commentState, membersState) => {
    switch (commentState.type) {
        case 'COMMENT_CREATE_PENDING':
        case 'REPLY_CREATE_PENDING': {
            if (membersState.currentOwner.type === 'MEMBER' || commentState.asGuest) {
                return membersState.currentOwner;
            }
            return ownerCache.getPlaceholder();
        }
        case 'COMMENT_CREATE_ERROR_EDITING':
        case 'COMMENT_CREATE_ERROR':
        case 'REPLY_CREATE_ERROR':
        case 'REPLY_CREATE_ERROR_EDITING': {
            if (membersState.currentOwner.type === 'MEMBER' || commentState.asGuest) {
                return membersState.currentOwner;
            }
            return ownerCache.getUnknown();
        }
        case 'REPLY_TYPING': {
            return membersState.currentOwner;
        }
        case 'COMMENT_LIVE_PENDING':
        case 'REPLY_LIVE_PENDING': {
            const createdBy = commentState.createdBy;
            return resolveOwner({ createdBy, membersState, fallback: ownerCache.getPlaceholder() });
        }
        case 'REPLY_DELETED':
        case 'COMMENT_DELETED': {
            return ownerCache.getUnknown();
        }
        default:
            const createdBy = commentState.comment.createdBy;
            return resolveOwner({ createdBy, membersState, fallback: ownerCache.getUnknown() });
    }
};
export const getOwnerName = (owner, t) => {
    var _a, _b;
    switch (owner.type) {
        case 'MEMBER': {
            return (_b = (_a = owner.member.profile) === null || _a === void 0 ? void 0 : _a.nickname) !== null && _b !== void 0 ? _b : t('user-info.unknown-member');
        }
        case 'ANONYMOUS': {
            return t('user-info.guest-commenter', { id: undefined });
        }
        case 'PLACEHOLDER': {
            // To preserve vertical space - unbreakable space
            return '\u00A0';
        }
        case 'UNKNOWN': {
            return t('user-info.unknown-member');
        }
        default:
            throw unreachable(owner);
    }
};
const resolveOwner = ({ createdBy, membersState, fallback, }) => {
    if (!createdBy) {
        return fallback;
    }
    if (createdBy.identityType === IdentityType.MEMBER) {
        const member = createdBy.identityId && membersState.membersCache[createdBy.identityId];
        return member ? ownerCache.getMember(member) : fallback;
    }
    if (createdBy.identityType === IdentityType.ANONYMOUS || !createdBy.identityType) {
        return createdBy.identityId ? ownerCache.getAnonymous(createdBy.identityId) : fallback;
    }
    return fallback;
};
export const getReactionOwner = (membersCache, id) => {
    const member = membersCache[id];
    // we can't check by IdentityType, as reactions always have member IdentityType :(
    return member ? ownerCache.getMember(member) : ownerCache.getAnonymous(id);
};
export const getReactionOwnerId = (owner) => owner.type === 'MEMBER' ? owner.member.id : owner.identityId;
export const getFollowedMemberById = (state, id) => state.members.followedMembers.find((m) => m.id === id);
//# sourceMappingURL=members-selectors.js.map